/**
 * Meta (Facebook) Pixel helper
 *
 * Thin wrapper around the global `fbq` that the base pixel snippet in
 * index.html installs. Every browser event fired through here is designed to
 * be deduplicated against the matching server-side Conversions API (CAPI)
 * event by Meta, using a shared, deterministic `event_id`.
 *
 * The `event_id` builders below MUST stay byte-for-byte identical to the
 * backend CAPI implementation. They are the dedup keys. Do not change the
 * format.
 */

import logger from "./logger";

/**
 * Fire a Meta Pixel `track` event. No-ops if the pixel never loaded
 * (blocked, offline, ad-blocker) so callers never have to guard `window.fbq`.
 *
 * @param {string} eventName - Standard Meta event name (e.g. "Purchase").
 * @param {Object} [params={}] - Event parameters (value, currency, ...).
 * @param {Object} [options={}] - Meta options, notably `{ eventID }` for CAPI dedup.
 */
export const metaTrack = (eventName, params = {}, options = {}) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") {
    return;
  }

  try {
    window.fbq("track", eventName, params, options);
  } catch (error) {
    // Never let analytics break a user flow.
    logger.error("Meta Pixel track failed", error);
  }
};

/**
 * Deterministic dedup key for a Purchase event. Shared with the backend CAPI.
 * @param {string} transactionId - Internal transaction id (e.g. "txn_...").
 * @returns {string}
 */
export const purchaseEventId = (transactionId) => `purchase_${transactionId}`;

/**
 * Deterministic dedup key for a CompleteRegistration event. Shared with the backend CAPI.
 * @param {string} userId - Supabase auth user id.
 * @returns {string}
 */
export const registrationEventId = (userId) => `registration_${userId}`;

/**
 * Deterministic dedup key for an InitiateCheckout event. Shared with the backend CAPI,
 * which rebuilds this exact string from the JWT subject and metadata.offer_id.
 *
 * Keyed on player + offer rather than on the individual page view: a player who
 * bounces off the same package three times is one unit of purchase intent, not
 * three, and Meta should not be trained to chase the re-openers.
 *
 * @param {string} playerId - Supabase auth user id.
 * @param {string} offerId - Coin package id being checked out.
 * @returns {string}
 */
export const initiateCheckoutEventId = (playerId, offerId) =>
  `checkout_${playerId}_${offerId}`;
